/**
 * Advisory panel — government travel, health, security, maritime, and aviation advisories.
 *
 * Filters events where source === 'advisory' and groups by category.
 * Category tabs: All, Travel, Health, Security, Maritime, Aviation.
 * Click event → fly to advisory country centroid.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';
import { getCountryCentroid } from '@/config/country-centroids';
import type { AdvisoryCategory } from '@/config/security-advisories';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

const CATEGORY_TABS: { label: string; value: AdvisoryCategory | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Travel', value: 'travel' },
  { label: 'Health', value: 'health' },
  { label: 'Security', value: 'security' },
  { label: 'Maritime', value: 'maritime' },
  { label: 'Aviation', value: 'aviation' },
];

export function createAdvisoryPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'advisory-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const tabBar = document.createElement('div');
  tabBar.style.cssText = `
    display: flex; gap: 4px; padding: 8px 12px; flex-wrap: wrap;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(tabBar);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeTab: AdvisoryCategory | 'all' = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderTabs(): void {
    tabBar.textContent = '';
    for (const tab of CATEGORY_TABS) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 11px; cursor: pointer; transition: all 150ms;
        background: ${activeTab === tab.value ? 'var(--accent)' : 'var(--bg-secondary)'};
        color: ${activeTab === tab.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeTab = tab.value;
        renderTabs();
        scheduleRender();
      };
      tabBar.appendChild(btn);
    }
  }

  function inferCategory(ev: NormalizedEvent): AdvisoryCategory {
    const tags = ev.tags ?? [];
    for (const tag of tags) {
      if (tag === 'travel' || tag === 'health' || tag === 'security' || tag === 'maritime' || tag === 'aviation') {
        return tag;
      }
    }
    const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
    if (text.includes('maritime') || text.includes('piracy') || text.includes('vessel')) return 'maritime';
    if (text.includes('aviation') || text.includes('notam') || text.includes('airspace')) return 'aviation';
    if (text.includes('health') || text.includes('disease') || text.includes('outbreak') || text.includes('cdc') || text.includes('who')) return 'health';
    if (text.includes('terrorism') || text.includes('security') || text.includes('interpol')) return 'security';
    return 'travel';
  }

  function render(): void {
    const events = appStore.getState().events;
    let advisoryEvents = events
      .filter(e => e.source === 'advisory')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Apply category filter
    if (activeTab !== 'all') {
      advisoryEvents = advisoryEvents.filter(e => inferCategory(e) === activeTab);
    }

    contentArea.textContent = '';

    // Count header
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${advisoryEvents.length > 0 ? 'var(--severity-high)' : 'var(--text-muted)'};`;
    countNum.textContent = String(advisoryEvents.length);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `active advisor${advisoryEvents.length !== 1 ? 'ies' : 'y'}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (advisoryEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No active advisories';
      contentArea.appendChild(msg);
      return;
    }

    // Group by target country
    const byCountry = new Map<string, NormalizedEvent[]>();
    for (const ev of advisoryEvents) {
      const country = ev.location?.country ?? ev.location?.countryCode ?? 'Unknown';
      const group = byCountry.get(country);
      if (group) group.push(ev);
      else byCountry.set(country, [ev]);
    }

    // Sort countries by most severe advisory first
    const severityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    const sortedCountries = Array.from(byCountry.entries()).sort((a, b) => {
      const aMax = Math.max(...a[1].map(e => severityRank[e.severity ?? 'info'] ?? 0));
      const bMax = Math.max(...b[1].map(e => severityRank[e.severity ?? 'info'] ?? 0));
      return bMax - aMax;
    });

    let rendered = 0;
    for (const [country, countryEvents] of sortedCountries) {
      if (rendered >= 50) break;

      // Country group header (only when multiple countries)
      if (sortedCountries.length > 1) {
        const groupHeader = document.createElement('div');
        groupHeader.style.cssText = `
          font-size: 11px; font-weight: 600; color: var(--text-secondary);
          margin: 8px 0 4px; padding: 4px 0;
          border-bottom: 1px solid var(--border-primary);
          text-transform: uppercase; letter-spacing: 0.05em;
        `;
        const countryCode = countryEvents[0]?.location?.countryCode ?? '';
        groupHeader.textContent = `${country}${countryCode ? ` (${countryCode})` : ''} — ${countryEvents.length}`;
        contentArea.appendChild(groupHeader);
      }

      for (const ev of countryEvents) {
        if (rendered >= 50) break;
        rendered++;

        const row = document.createElement('div');
        const sev = ev.severity ?? 'info';
        row.style.cssText = `
          padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
          background: var(--bg-secondary); border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
          cursor: pointer;
        `;

        // Click → fly to country centroid
        row.onclick = () => {
          const code = ev.location?.countryCode;
          if (code) {
            const centroid = getCountryCentroid(code);
            if (centroid) {
              const map = getMap();
              if (map) {
                map.flyTo({ center: [centroid[1], centroid[0]], zoom: 5, duration: 600 });
              }
            }
          } else if (ev.location) {
            const map = getMap();
            if (map) {
              map.flyTo({ center: [ev.location.lng, ev.location.lat], zoom: 5, duration: 600 });
            }
          }
        };
        row.title = 'Click to fly to location';

        // Top line: issuing country code + title
        const titleRow = document.createElement('div');
        titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px;';

        const issuerBadge = document.createElement('span');
        issuerBadge.style.cssText = `
          font-size: 9px; font-weight: 600; padding: 1px 4px; border-radius: 3px;
          background: var(--bg-primary); color: var(--text-muted);
          font-family: var(--font-mono);
        `;
        issuerBadge.textContent = ev.location?.countryCode ?? '??';
        titleRow.appendChild(issuerBadge);

        const titleEl = document.createElement('span');
        titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
        titleEl.textContent = ev.title;
        titleRow.appendChild(titleEl);

        // Severity badge
        const sevBadge = document.createElement('span');
        sevBadge.style.cssText = `
          font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px;
          text-transform: uppercase;
          background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'}22;
          color: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        `;
        sevBadge.textContent = sev;
        titleRow.appendChild(sevBadge);

        row.appendChild(titleRow);

        // Timestamp
        const time = document.createElement('div');
        time.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 2px;';
        time.textContent = relativeTime(ev.timestamp);
        row.appendChild(time);

        contentArea.appendChild(row);
      }
    }
  }

  // Initial render
  renderTabs();
  render();

  // Subscribe to event changes
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
